// Mirror of Tina's one festival page API (festival Plans 7 and 8), field for field: snake_case as the orchestrator
// sends it, days as `YYYY-MM-DD`, times as ISO strings, UUIDs as strings. Sources, read-only in the Tina repo:
// `src/orchestrator/schemas/festival_page.py` (the reads), `src/db/schemas/festival.py` (the request bodies and the
// vocabularies), `src/festival/suggestions.py` (`SuggestionView`), `src/festival/preview.py` (`PreviewLine`) and
// `src/festival/status.py` (`FestivalStatus`). A rename or a type change there is a change here. Semantics: the
// `tina-festivals` skill in the Tina repo.
//
// Vocabularies are `as const` tuples with a union derived from them, so a label map typed `Record<Union, string>`
// stops compiling when Tina adds a member.
import type {
  ChoiceOutcome,
  DaySource,
  FestivalKind,
  FestivalRecordState,
  MatchedBy,
  PageChoice,
} from "@/types/tina-festivals";

// ── Vocabularies ────────────────────────────────────────────────────────────

/** The nine statuses of a festival, in Plan 7's order. Computed by Tina from stored facts, never stored. */
export const FESTIVAL_STATUSES = [
  "waiting_approval",
  "waiting_venue",
  "group_waiting",
  "publishing",
  "live",
  "live_changes",
  "failed",
  "rejected",
  "ended_unapproved",
] as const;
export type FestivalStatus = (typeof FESTIVAL_STATUSES)[number];

/** What a curator chooses for a Mood event already in the festival's place. */
export const EXISTING_CHOICES = ["replace", "use_as_day", "not_part"] as const;
export type ExistingChoiceKind = (typeof EXISTING_CHOICES)[number];

export const PUBLISH_STEP_KINDS = [
  "update_day",
  "make_day",
  "group",
  "replace",
  "not_part",
  "remove_day",
  "rename_group",
  "delete_group",
] as const;
export type PublishStepKind = (typeof PUBLISH_STEP_KINDS)[number];

export const PUBLISH_STEP_STATES = [
  "pending",
  "running",
  "done",
  "needs_you",
  "failed",
  "skipped",
] as const;
export type PublishStepState = (typeof PUBLISH_STEP_STATES)[number];

/** The fields of a Mood event the page compares and previews. */
export const MOOD_FIELDS = [
  "name",
  "date",
  "venue",
  "lineup",
  "cancelled",
] as const;
export type MoodField = (typeof MOOD_FIELDS)[number];

export const SUGGESTION_KINDS = [
  "new_day",
  "moved_day",
  "day_unlisted",
  "new_name",
  "conflict",
  "lineup",
  "cancel",
  "not_added",
  "mood_changed",
  "mood_deleted",
] as const;
export type SuggestionKind = (typeof SUGGESTION_KINDS)[number];

/** What an Accept of a suggestion needs from the curator. */
export const SUGGESTION_NEEDS = ["artist", "venue", "choice"] as const;
export type SuggestionNeed = (typeof SUGGESTION_NEEDS)[number];

export const REREAD_STATES = ["active", "dead", "retired"] as const;
export type RereadState = (typeof REREAD_STATES)[number];

export const REREAD_OUTCOMES = [
  "read",
  "no_festival",
  "strike",
  "infra",
  "cancelled",
  "ended",
  "unlisted",
] as const;
export type RereadOutcome = (typeof REREAD_OUTCOMES)[number];

export type FoundBy = "tina" | "mood_search";

/** Whether Mood shows an "Already in Mood" event, as the last Admin API search said. */
export type MoodVisibility = "live" | "draft" | "hidden";

export type PreviewAction =
  | "make"
  | "update"
  | "group"
  | "delete"
  | "detach"
  | "cancel"
  | "rename";

/** How a day's Mood event stands against what Tina wrote. */
export type DayMoodState = "in_mood" | "changed_in_mood" | "deleted_in_mood";

/** Whether the page cancels some days or all of them. */
export type Cancellation = "none" | "some_days" | "festival";

// ── Reads ───────────────────────────────────────────────────────────────────

/** One line of what Tina will make in Mood. The text is Tina's; IMS shows it as it comes. */
export interface PreviewLine {
  action: PreviewAction;
  text: string;
}

/** One act of a day's lineup. `photo_url` is null for a pre-filled or free-text entry (shown as initials). */
export interface LineupEntryRead {
  name: string;
  artist_id: string | null;
  set_time: string | null;
  stage: string | null;
  photo_url: string | null;
}

export interface DayBox {
  day: string;
  day_number: number;
  venue_id: string | null;
  venue_name: string | null;
  lineup: LineupEntryRead[];
  sources: DaySource[];
  blocker: "venue_missing" | null;
  mood_event_id: string | null;
  state: PublishStepState | null;
  not_added: string[];
  error: string | null;
  /** The day event's name (what Tina wrote, else the name it will get). */
  name: string | null;
  cancelled: boolean;
  cancelled_in_mood: boolean;
  mood_state: DayMoodState | null;
  /** The event's saves as Tina last read them; null: unknown. */
  saves: number | null;
}

export interface ExistingRead {
  mood_event_id: string;
  name: string;
  service_day: string | null;
  start_iso: string | null;
  end_iso: string | null;
  venue_id: string | null;
  venue_name: string | null;
  saves: number | null;
  found_by: FoundBy;
  matched_by: MatchedBy | null;
  choice: ExistingChoiceKind;
  target_day: string | null;
  /** An event a "Needs you" step found that the curator never listed. */
  suggested: boolean;
  outcome: ChoiceOutcome | null;
  visibility: MoodVisibility | null;
}

export interface RereadRead {
  state: RereadState;
  last_read_at: string | null;
  strikes: number;
  last_outcome: RereadOutcome | null;
}

/** One page Tina read for the festival. `own_page` is sent back as read, or the mark is cleared. */
export interface FestivalPageView {
  url: string;
  site: string;
  read_at: string;
  member: boolean;
  held: boolean;
  own_page: boolean;
  reread: RereadRead | null;
}

export interface ConcertRead {
  mood_event_id: string;
  name: string;
  service_day: string | null;
  venue_id: string | null;
  venue_name: string | null;
  in_group: boolean;
  found_by: FoundBy;
  visibility: MoodVisibility | null;
}

export interface PublishStepDetail {
  not_added: string[];
  conflict_event_id: string | null;
  conflict_event_name: string | null;
  group_id: string | null;
  image_flagged: string | null;
  fields: MoodField[];
  delete_in_mood: boolean;
}

export interface PublishStepRead {
  step: PublishStepKind;
  subject: string;
  day: string | null;
  state: PublishStepState;
  outcome: string | null;
  mood_event_id: string | null;
  detail: PublishStepDetail;
  error: string | null;
  attempts: number;
  updated_at: string;
}

export interface PublishRead {
  seq: number;
  days_done: number;
  days_total: number;
  steps: PublishStepRead[];
}

/** One open suggestion of an approved page: Tina's `SuggestionView` with the card's id, pages and opening time. */
export interface SuggestionRead {
  id: string;
  kind: SuggestionKind;
  text: string;
  day: string | null;
  to_day: string | null;
  venue_id: string | null;
  venue_name: string | null;
  name: string | null;
  mood_event_id: string | null;
  default_choice: ExistingChoiceKind | null;
  fields: MoodField[];
  needs: SuggestionNeed[];
  sources: DaySource[];
  created_at: string;
}

/** One festival's whole page (`GET /festivals/pages/{record_id}`). */
export interface FestivalPageRead {
  record_id: string;
  state: FestivalRecordState;
  kind: FestivalKind;
  name: string;
  other_names: string[];
  city: string | null;
  first_day: string | null;
  last_day: string | null;
  status: FestivalStatus;
  status_label: string;
  site_count: number;
  tray: string[];
  days: DayBox[];
  existing: ExistingRead[];
  pages: FestivalPageView[];
  concerts: ConcertRead[];
  preview: PreviewLine[];
  publish: PublishRead | null;
  /** Approve is off while there is one: `venue_missing:<day>`, `no_poster`. */
  blockers: string[];
  /** Sent back with an approval; null exactly when the record has no skeleton. */
  skeleton_digest: string | null;
  backend_group_id: string | null;
  mood_hits_at: string | null;
  /** Ask "Day ticket or Program?" first. */
  kind_question: boolean;
  suggestions: SuggestionRead[];
  /** What Publish changes would do now. */
  changes_preview: PreviewLine[];
  changes_waiting: boolean;
  /** What Publish changes sends back; null before approval. */
  page_version: string | null;
  cancellation: Cancellation;
  /** A curator took the festival out of Mood: the page keeps everything and its main button reads "Approve and publish". */
  unpublished: boolean;
}

/** One festival of the list (`GET /festivals/pages`). */
export interface FestivalListRow {
  record_id: string;
  name: string;
  kind: FestivalKind;
  city: string | null;
  first_day: string | null;
  last_day: string | null;
  status: FestivalStatus;
  status_label: string;
  site_count: number;
  updated_at: string;
}

/** What a page-scoped split answers: the original's page and the id of the record that took the pages. */
export interface FestivalSplitPageResult {
  page: FestivalPageRead;
  new_record_id: string;
}

/** What the dry-run route answers. `lines` is empty whenever `problems` is not. */
export interface FestivalPreviewRead {
  lines: PreviewLine[];
  problems: string[];
}

// ── Bodies (without `resolved_by`: the repository adds it) ──────────────────

export interface DayLineupEntry {
  name: string;
  artist_id: string | null;
  set_time: string | null;
  stage: string | null;
  /** Kept by Tina only with an `artist_id` and an http(s) url. */
  photo_url: string | null;
}

export interface ApproveDayIn {
  day: string;
  venue_id: string;
  venue_name: string;
  lineup: DayLineupEntry[];
}

export interface ExistingChoiceIn {
  mood_event_id: string;
  choice: ExistingChoiceKind;
  /** The day it folds into or becomes; null for `not_part`. */
  day: string | null;
}

export interface ApproveDayTicketBody {
  canonical_name: string;
  aliases: string[];
  days: ApproveDayIn[];
  existing: ExistingChoiceIn[];
  pages: PageChoice[];
  poster_url: string | null;
  skeleton_digest: string;
}

export interface ApproveGroupBody {
  skeleton_digest: string;
}

export interface PublishRetryBody {
  existing: ExistingChoiceIn[];
}

export interface ChangeDayIn {
  day: string;
  /** The stored date the day moved from; only when it moved. */
  from_day: string | null;
  venue_id: string;
  venue_name: string;
  lineup: DayLineupEntry[];
  cancelled: boolean;
}

export interface RemovedDayIn {
  day: string;
  delete_in_mood: boolean;
}

export interface PublishChangesBody {
  canonical_name: string;
  aliases: string[];
  days: ChangeDayIn[];
  removed: RemovedDayIn[];
  existing: ExistingChoiceIn[];
  pages: PageChoice[];
  tray_dropped: string[];
  page_version: string;
}

/** The answer fields of an Accept; which apply depends on the suggestion's kind. */
export interface SuggestionAcceptBody {
  artist_id?: string | null;
  photo_url?: string | null;
  venue_id?: string | null;
  venue_name?: string | null;
  choice?: ExistingChoiceKind | null;
  day?: string | null;
  make_canonical?: boolean;
}

/** The body IMS would send to approve (before approval) or publish changes (after). */
export type PreviewBody = ApproveDayTicketBody | PublishChangesBody;
